// Fx render pass: ripples (landed / go-around / crash), the score marks an
// arrival leaves where it touched down, and idle ink (the doodle a finger
// leaves on empty sky). Ported 1:1 out of `canvas.ts`'s `draw()`; only
// `this.ctx` → an explicit `frame.ctx`, and the cosmetic arrays the Renderer
// owns (`ripples`, `scores`, `doodles`, `liveDoodle`) are threaded in as
// parameters instead of read off `this`.
import { COL, hexA, reduceMotion } from './theme.ts';
import { clamp } from '../sim/index.ts';
import type { FrameCtx } from './canvas.ts';

export interface Ripple {
  x: number; y: number; t: number; kind: 'landed' | 'goaround' | 'crash';
  /** `performance.now()` at ingestion — 'crash' only. The sim stops stepping
   *  the instant it crashes (world.state → 'dead'), so `t` (aged by SIM_DT in
   *  `onStep`) would otherwise freeze near zero forever; the failure bloom
   *  (Q4) is timed off the wall clock instead, independent of sim time. */
  wallStart?: number;
}
/** What an arrival paid, lifting off the touchdown point as it fades. */
export interface ScoreMark { x: number; y: number; text: string; t: number }
/** One point of idle ink, aged on the wall clock (see `doodleFade`). */
export interface InkDot { x: number; y: number; born: number }

export const RIPPLE_LIFE: Record<Ripple['kind'], number> = { landed: 1.4, goaround: 1.4, crash: 1.2 };
// The score of an arrival, written where it touched down. The HUD counter
// jumping by 5 or by 20 is not something anyone reads mid-approach; this is
// how the player learns that a heavy pays three lights, and it says so at the
// only moment they are looking at that spot anyway. Same life as the landing
// ripple it rides out on, drawn in the same slate, so it reads as one event.
export const SCORE_LIFE = RIPPLE_LIFE.landed;
/** how far (logical px) the number lifts over its life */
const SCORE_RISE = 20;
const SCORE_ALPHA = 0.55;

// Idle ink: the line a finger leaves when it goes down nowhere near an
// aircraft. It commands nothing, so it is drawn soft grey rather than in a
// plane's colour, sits under everything that matters, and dissolves from its
// tail — the reward for drawing on empty sky is the drawing itself.
/** how long (s) one point of idle ink survives */
const DOODLE_LIFE = 1.5;
/** fraction of that life it stays at full strength before fading */
const DOODLE_HOLD = 0.35;
/** the strongest the line ever gets */
const DOODLE_ALPHA = 0.4;
const DOODLE_WIDTH = 3;
/** minimum gap (logical px) between recorded points — a still finger would
 *  otherwise stack round caps on one spot and composite them into a dark blob */
export const DOODLE_SPACING = 3;
/** alpha steps the fade is quantised to, so a run of points that round to the
 *  same alpha is one stroke call instead of one per point */
const DOODLE_STEPS = 16;

/**
 * Fraction of `DOODLE_ALPHA` a point of idle ink still has at `age` seconds:
 * full for the first `DOODLE_HOLD` of its life, then a quadratic fade to
 * nothing. Exported for its own test — it is the whole shape of the effect.
 */
export function doodleFade(age: number): number {
  const t = age / DOODLE_LIFE;
  if (t <= DOODLE_HOLD) return 1;
  if (t >= 1) return 0;
  const k = (1 - t) / (1 - DOODLE_HOLD);
  return k * k;
}

export function drawRipples(frame: FrameCtx, ripples: Ripple[]): void {
  const { ctx } = frame;
  for (const r of ripples) {
    if (r.kind === 'crash') {
      // Q4 failure feel: a soft, low-alpha coral bloom at the collision
      // point, expanding and fading over ~1.2s of *wall-clock* time (see
      // the `wallStart` note on `Ripple`) — no shake, no red flash.
      const lifeMs = RIPPLE_LIFE.crash * 1000;
      const elapsedMs = r.wallStart !== undefined ? performance.now() - r.wallStart : lifeMs;
      const t = reduceMotion ? 0.5 : clamp(elapsedMs / lifeMs, 0, 1);
      if (t >= 1) continue;
      ctx.fillStyle = hexA(COL.coral, 0.26 * (1 - t));
      ctx.beginPath(); ctx.arc(r.x, r.y, 14 + t * 78, 0, Math.PI * 2); ctx.fill();
      continue;
    }
    const life = RIPPLE_LIFE[r.kind];
    const t = r.t / life;
    const color = r.kind === 'landed' ? COL.slate : COL.coral;
    ctx.strokeStyle = hexA(color, 0.45 * (1 - t));
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(r.x, r.y, 8 + t * 46, 0, Math.PI * 2); ctx.stroke();
  }
}

export function drawScores(frame: FrameCtx, scores: ScoreMark[]): void {
  if (!scores.length) return;
  const { ctx } = frame;
  ctx.save();
  ctx.font = '300 13px -apple-system,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const m of scores) {
    const t = m.t / SCORE_LIFE;
    // full strength for the first breath, then out with the ripple — a
    // number that starts fading immediately never gets read at all
    const a = SCORE_ALPHA * (t < 0.3 ? 1 : 1 - (t - 0.3) / 0.7);
    ctx.fillStyle = hexA(COL.slate, a);
    ctx.fillText(m.text, m.x, m.y - 14 - t * SCORE_RISE);
  }
  ctx.restore();
}

/** Age out expired idle ink and draw what is left. `frame.now` is the
 *  frame's own `performance.now()` — this ink fades on the wall clock, not
 *  on sim time, so it keeps dissolving even when the sim has stopped
 *  stepping. `doodles` and `liveDoodle` are the Renderer's own cosmetic
 *  state, mutated in place exactly as the method version mutated `this.*`. */
export function drawDoodles(frame: FrameCtx, doodles: InkDot[][], liveDoodle: InkDot[] | null): void {
  if (!doodles.length) return;
  const { ctx, now } = frame;
  ctx.save();
  ctx.strokeStyle = COL.slateSoft;
  ctx.lineWidth = DOODLE_WIDTH;
  // butt, not round: consecutive alpha bands share their boundary point, and
  // two round caps meeting there composite over each other — every boundary
  // came out as a visible bead threaded along the fading line. Flat ends abut
  // exactly instead. `lineJoin` still rounds every interior corner, and the
  // wedge a butt cap leaves on the outside of a turn is under half a pixel at
  // this width.
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'round';
  let w = 0;
  for (const s of doodles) {
    let dead = 0;
    while (dead < s.length && (now - s[dead].born) / 1000 >= DOODLE_LIFE) dead++;
    if (dead) s.splice(0, dead);
    strokeDoodle(frame, s, now);
    if (s.length || s === liveDoodle) doodles[w++] = s;
  }
  doodles.length = w;
  ctx.restore();
}

/** One stroke of idle ink. Its points are in draw order, so the fade only
 *  ever rises along it and each quantised alpha band is one contiguous run —
 *  a long doodle costs at most `DOODLE_STEPS` stroke calls, not one a point. */
function strokeDoodle(frame: FrameCtx, pts: InkDot[], now: number): void {
  if (pts.length < 2) return;
  const { ctx } = frame;
  const band = (i: number): number => Math.round(doodleFade((now - pts[i].born) / 1000) * DOODLE_STEPS);
  let i = 0;
  while (i < pts.length - 1) {
    const b = band(i);
    let j = i + 1;
    while (j < pts.length - 1 && band(j) === b) j++;
    if (b > 0) {
      ctx.globalAlpha = (b / DOODLE_STEPS) * DOODLE_ALPHA;
      ctx.beginPath();
      ctx.moveTo(pts[i].x, pts[i].y);
      for (let k = i + 1; k <= j; k++) ctx.lineTo(pts[k].x, pts[k].y);
      ctx.stroke();
    }
    i = j; // runs share their boundary point, so the line stays unbroken
  }
}
